'use client'

import { useRef, useEffect, useState } from 'react'

import { VideoPlayer, VideoPlayerRef } from '@/components/common/player'
import { useTaskRatio } from '@/hooks/utils'
import { useRouter } from '@/i18n/navigation'

import { useWorkContext } from '../work-context'

const CONTAINER_PADDING = 48 // p-6 = 24px × 2

export const DesktopPlayContainer = () => {
  const { data } = useWorkContext()
  const playerRef = useRef<VideoPlayerRef>(null)
  const playerContainerRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  const aspectRatio = useTaskRatio({
    aspectRatio: data?.extra?.aspectRatioV2,
    orientation: data?.extra?.aspectRatio
  })

  const [srcReady, setSrcReady] = useState(false)
  const [playerStyle, setPlayerStyle] = useState<React.CSSProperties>(() => ({
    width: '100%',
    maxHeight: '100%',
    aspectRatio: (aspectRatio || '16/9').replace('/', ' / ')
  }))

  // 使用 ResizeObserver 精确计算播放器尺寸，避免 CSS aspect-ratio 在 flex 布局中的兼容性问题
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const [rw, rh] = aspectRatio.split('/').map(Number)
    const ratio = rw / rh

    const update = () => {
      const availW = container.offsetWidth - CONTAINER_PADDING
      const availH = container.offsetHeight - CONTAINER_PADDING
      if (availW <= 0 || availH <= 0) return

      // 将最大矩形拟合到可用区域内，保持比例
      const heightFromWidth = availW / ratio
      if (heightFromWidth <= availH) {
        setPlayerStyle({ width: availW, height: heightFromWidth })
      } else {
        setPlayerStyle({ width: availH * ratio, height: availH })
      }
    }

    update()
    const ro = new ResizeObserver(update)
    ro.observe(container)
    return () => ro.disconnect()
  }, [aspectRatio])

  // 只在播放器外部区域点击时返回上一页
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const container = containerRef.current
      const playerContainer = playerContainerRef.current
      if (!container || !playerContainer) return
      if (
        container.contains(event.target as Node) &&
        !playerContainer.contains(event.target as Node)
      ) {
        router.back()
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [router])

  // 桌面端：空闲后再挂视频，避免和首屏抢带宽
  useEffect(() => {
    const src = data?.videoUrl
    if (!src || !srcReady) return

    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let canPlayHandler: (() => void) | null = null

    const tryAutoPlay = (retriesLeft = 20) => {
      if (cancelled) return

      const video = playerContainerRef.current?.querySelector('video') as HTMLVideoElement | null
      if (!video) {
        if (retriesLeft > 0) {
          retryTimer = setTimeout(() => tryAutoPlay(retriesLeft - 1), 100)
        }
        return
      }

      const playNow = () => {
        if (cancelled) return
        video.playsInline = true
        video.play().catch(() => {})
      }

      if (video.readyState >= 3) {
        playNow()
        return
      }

      canPlayHandler = () => {
        playNow()
      }
      video.addEventListener('canplay', canPlayHandler, { once: true })
    }

    tryAutoPlay()

    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
      if (canPlayHandler) {
        const video = playerContainerRef.current?.querySelector('video') as HTMLVideoElement | null
        video?.removeEventListener('canplay', canPlayHandler)
      }
    }
  }, [data?.videoUrl, srcReady])

  return (
    <div ref={containerRef} className="flex h-full w-full items-center justify-center p-6">
      <div
        ref={playerContainerRef}
        className="relative overflow-hidden rounded-lg"
        style={playerStyle}
        onPointerDown={() => setSrcReady(true)}
      >
        <VideoPlayer
          ref={playerRef}
          className="cursor-pointer"
          classNames={{ container: 'size-full' }}
          fit="fill"
          load="idle"
          src={srcReady ? data?.videoUrl : undefined}
          controls={{
            visibility: 'hover',
            showProgress: true,
            showDownload: true,
            showVolume: true,
            showFullscreen: true,
            showTime: true
          }}
          loading={{
            showOnInitial: true,
            showOnBuffering: true
          }}
          playback={{
            autoPlay: true,
            loop: true,
            muted: false
          }}
          playButton={{
            visibility: 'paused',
            showOnlyAfterStart: true
          }}
          poster={{
            src: data?.thumbnail ?? '',
            loading: 'eager',
            priority: true,
            imageProps: { sizes: '(max-width: 768px) 100vw, 80vw', unoptimized: true }
          }}
        />
      </div>
    </div>
  )
}
